import { useMemo, useState } from "react";
import type { Paper } from "../types/api";
import { displayDash, formatEvidenceLevel, paperLink } from "../utils/format";
import {
  isOutlier,
  levelBadgeClass,
  sortPapers,
  type SortDir,
  type SortKey,
} from "../utils/papers";

interface EvidenceTableProps {
  papers: Paper[];
}

export function EvidenceTable({ papers }: EvidenceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("final");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sorted = useMemo(() => sortPapers(papers, sortKey, sortDir), [papers, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "title" ? "asc" : "desc");
    }
  };

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  if (!papers.length) {
    return (
      <div className="empty-evidence">
        <p>
          No evidence retrieved. The local index may not cover this topic — try broader or
          differently phrased terms.
        </p>
      </div>
    );
  }

  return (
    <div className="evidence-table-wrap">
      <p className="evidence-count">{papers.length} references</p>
      <div className="table-scroll">
        <table className="evidence-table">
          <thead>
            <tr>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("title")}>
                  Title{arrow("title")}
                </button>
              </th>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("rcs")}>
                  RCS{arrow("rcs")}
                </button>
              </th>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("level")}>
                  Level{arrow("level")}
                </button>
              </th>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("cited")}>
                  Cited{arrow("cited")}
                </button>
              </th>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("year")}>
                  Year{arrow("year")}
                </button>
              </th>
              <th>
                <button type="button" className="th-btn" onClick={() => toggleSort("journalQ")}>
                  Journal Q{arrow("journalQ")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => {
              const link = paperLink(p);
              const outlier = isOutlier(p);
              return (
                <tr
                  key={`${p.pmcid ?? p.doi ?? i}`}
                  className={`${outlier ? "outlier" : ""}${p.is_retracted ? " retracted" : ""}`}
                >
                  <td>
                    {link ? (
                      <a href={link} target="_blank" rel="noreferrer" className="paper-title-link">
                        {p.title ?? "Unknown"}
                      </a>
                    ) : (
                      <span>{p.title ?? "Unknown"}</span>
                    )}
                    <span className="paper-sub">
                      {[p.journal, p.pmcid].filter(Boolean).join(" · ")}
                    </span>
                    {p.is_retracted ? <span className="retracted-tag">RETRACTED</span> : null}
                  </td>
                  <td className="mono-bold">{displayDash(p.rcs_score)}</td>
                  <td>
                    <span className={`level-badge ${levelBadgeClass(p.evidence_level)}`}>
                      {formatEvidenceLevel(p.evidence_level)}
                    </span>
                  </td>
                  <td>{displayDash(p.citation_count)}</td>
                  <td>{displayDash(p.year)}</td>
                  <td>{displayDash(p.journal_quality)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
